import styled, { keyframes } from 'styled-components';

/* soft, slow "breathing" glow — gentle on the eyes even with many tiles */
const pulseRed = keyframes`
  0%, 100% { border-color: #a83a3a; box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(217,65,65,0.28), 0 0 8px 1px rgba(217,65,65,0.20); }
  50%      { border-color: #f26060; box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(217,65,65,0.55), 0 0 16px 3px rgba(217,65,65,0.45); }
`;
const pulseAmber = keyframes`
  0%, 100% { border-color: #b3752a; box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(224,147,47,0.26), 0 0 8px 1px rgba(224,147,47,0.18); }
  50%      { border-color: #f0a94a; box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(224,147,47,0.50), 0 0 16px 3px rgba(224,147,47,0.44); }
`;
const pulseBlue = keyframes`
  0%, 100% { border-color: #277a97; box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(47,158,196,0.28), 0 0 8px 1px rgba(34,182,240,0.22); }
  50%      { border-color: #43b6e6; box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(47,158,196,0.48), 0 0 16px 3px rgba(34,182,240,0.44); }
`;

const dashRun = keyframes`
  to { stroke-dashoffset: -14; }
`;

/* пунктирный контур, «бегущий» по периметру плитки (статус «Проверка») */
export const CheckingBorder = styled.svg`
  position: absolute;
  inset: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  pointer-events: none;
  overflow: visible;
  rect {
    fill: none;
    stroke: #8794a1;
    stroke-width: 1.5;
    stroke-dasharray: 5 2;
    animation: ${dashRun} 0.9s linear infinite;
  }
`;

export const Content = styled.div`
  cursor: pointer;
  position: relative;
  width: 56px;
  min-height: 84px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 3px;
  padding: 4px 4px 4px;
  border-radius: 6px;
  background-color: #1b1f26;
  border: 1px solid #3a4450;
  /* idle: near-flat — thin border + subtle inset, only a whisper of green so a dense scene stays calm */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 0 5px rgba(92, 201, 138, 0.05);
  transition: box-shadow 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;

  /* active call — soft blue breathing border only (no fill) */
  &.with-call {
    border-color: #2f9ec4;
    animation: ${pulseBlue} 2.4s ease-in-out infinite;
  }

  /* notification / warning — soft amber breathing */
  &.state-notification {
    border-color: #e0932f;
    animation: ${pulseAmber} 2.4s ease-in-out infinite;
  }

  /* critical / alarm — soft red breathing (wins over notification) */
  &.state-critical {
    border-color: #d94141;
    animation: ${pulseRed} 2.4s ease-in-out infinite;
  }

  /* нет связи — приглушённая плитка без свечения */
  &.offline {
    border-color: #2c353d;
    background-color: #171b21;
    animation: none;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.02);
  }
  &.offline img {
    opacity: 0.4;
    filter: grayscale(1);
  }

  /* проверка связи — статус ещё не пришёл: пунктирный бордюр, «бегущие» штрихи */
  &.checking {
    border-color: transparent;
    animation: none;
  }
  &.checking img {
    opacity: 0.6;
  }
`;

/* серый индикатор «нет связи» в углу плитки */
export const OfflineDot = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7a838a;
  box-shadow: 0 0 0 2px #171b21;
  z-index: 2;
`;

/* plain positioning wrapper (no frame) */
export const RackWrapper = styled.div`
  position: relative;
  display: inline-flex;
  cursor: pointer;
  &:hover {
    z-index: 100;
  }
`;

/* barriers laid out in a centered row (1 or 2), pinned to the top */
export const Barriers = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

export const Barrier = styled.div`
  width: 16px;
  height: 9px;
  background: url('/images/barrier.svg') no-repeat center;
  background-size: contain;
`;

export const Id = styled.div`
  font-size: 11px;
  font-weight: bold;
  line-height: 1.2;
  color: #171a1f;
  text-align: center;
  white-space: nowrap;
  padding: 1px 6px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.92);
  &.offline {
    opacity: 0.55;
  }
`;

export const IconTitle = styled.div`
  position: relative;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  img {
    width: 44px;
    height: 44px;
    object-fit: contain;
    display: block;
  }
`;

/* hover card — "what's happening on this rack", CleverParking style */
export const Tip = styled.div`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  width: 244px;
  box-sizing: border-box;
  background: #161e23;
  border: 1px solid #3d4952;
  border-radius: 12px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.55);
  padding: 12px 14px 11px;
  z-index: 120;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #3d4952;
  }
  ${RackWrapper}:hover & {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  /* при входящем звонке Tip ловит мышь (вместе с мостиком), чтобы можно было дойти до кнопок над ним */
  ${(props) =>
    props.$call
      ? `
    ${RackWrapper}:hover & {
      pointer-events: auto;
    }
    &::before {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      height: 12px;
    }
  `
      : ''}
`;

export const TipTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #e6eaec;
  line-height: 1.25;
  margin-bottom: 8px;
`;

export const TipRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
  & + & {
    border-top: 1px solid #232e35;
  }
`;

export const Dot = styled.span`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-top: 4px;
  border-radius: 50%;
  background: #7a838a;
  &.critical {
    background: #d94141;
    box-shadow: 0 0 6px rgba(217, 65, 65, 0.6);
  }
  &.notification {
    background: #e0932f;
    box-shadow: 0 0 6px rgba(224, 147, 47, 0.6);
  }
`;

export const TipMsg = styled.div`
  font-size: 13.5px;
  color: #aab2b8;
  line-height: 1.35;
`;

export const TipOk = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: #5cc98a;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #47b972;
    box-shadow: 0 0 6px rgba(71, 185, 114, 0.6);
  }
`;

export const TipOffline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: #aab2b8;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #7a838a;
    box-shadow: 0 0 6px rgba(122, 131, 138, 0.5);
  }
`;

export const TipHint = styled.div`
  margin-top: 9px;
  padding-top: 8px;
  border-top: 1px solid #232e35;
  font-size: 12px;
  letter-spacing: 0.02em;
  color: #7a838a;
  text-align: center;
`;
